import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { AccountSelectors, getProductCodename } from '@recruitee/account';
import { filterBoolean } from '@recruitee/common';
import { DevFlag, injectDevFlag } from '@recruitee/dev-flags/data';
import { FeatureSpecSelectors } from '@recruitee/feature-spec/data';
import { BillingPlanTier, Company } from '@recruitee/http/types';
import { DialogService, DialogWidth } from '@recruitee/user-interface';

import { CodenameInvalidError } from './codename-invalid-error';
import { FeatureGuardDialogComponent } from './feature-guard-dialog/feature-guard-dialog.component';
import { FeaturesGuardModel } from './feature-guard.model';
import { FeatureGuardMode, GuardedFeature, legacyFeatureToCode } from './feature-guard.types';

@Injectable({ providedIn: 'root' })
export class FeatureGuardFacade {
  private useNewConfig = injectDevFlag(DevFlag.FeatureSpec);
  private currentCompany: Company;

  constructor(
    private dialogService: DialogService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private model: FeaturesGuardModel,
    private store: Store,
  ) {
    this.store
      .select(AccountSelectors.currentCompany)
      .pipe(filterBoolean)
      .subscribe(currentCompany => (this.currentCompany = currentCompany));
  }

  public showFeatureInfo(mode: FeatureGuardMode, feature: GuardedFeature) {
    switch (mode) {
      case FeatureGuardMode.Dialog:
        this.showDialog(feature);
        break;
      case FeatureGuardMode.Redirect:
        this.redirectToEmptyState(feature);
        break;
      default:
    }
  }

  public showDialog(feature: GuardedFeature) {
    this.dialogService.openCustom(
      FeatureGuardDialogComponent,
      {
        width: DialogWidth.Small,
      },
      { feature },
    );
  }

  public redirectToEmptyState(feature: GuardedFeature) {
    switch (feature) {
      case GuardedFeature.SendLater:
        this.router.navigate([`mailbox/upgrade`]);
        break;
      default:
    }
  }

  public getYTUrl(id: string): SafeResourceUrl {
    // SECURITY: id is a youtube's ID like "z9iA6tYk_KI" - we can trust it
    // But just in case, we'll check whether it's not too long
    if (id && id.length > 15) {
      throw new TypeError(`Invalid VideoDialog#videoId: ${id}`);
    }

    const url = `https://www.youtube.com/embed/${id}?autoplay=0&origin=https://recruitee.com`;
    // eslint-disable-next-line ban/ban
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  // eslint-disable-next-line max-lines-per-function
  public checkFeatureAvailability(
    feature: GuardedFeature | null,
    checkWithPlan: BillingPlanTier | false = false,
  ): Observable<boolean> {
    if (!this.useNewConfig || feature === null) {
      return this.legacyFeatureAvailability(feature, checkWithPlan);
    }

    const featureCode = legacyFeatureToCode[feature];
    if (!featureCode) {
      throw new Error(`FeatureCode ${feature} not found`);
    }
    if (checkWithPlan) {
      throw new Error(`checkWithPlan is not supported in new config`);
    }
    return this.store.select(FeatureSpecSelectors.forCode(featureCode)).pipe(
      filterBoolean,
      take(1), // TODO - remove take(1) when consumers start utilizing reactivity
      map(entity => entity.entitled),
    );
  }

  private legacyFeatureAvailability(
    feature: GuardedFeature | null,
    checkWithPlan: BillingPlanTier | false = false,
  ): Observable<boolean> {
    return this.store
      .select(AccountSelectors.currentCompany)
      .pipe(filterBoolean, first())
      .pipe(
        map(() => {
          const codename = getProductCodename(this.currentCompany);

          if (this.currentCompany.sympaDeal && feature === GuardedFeature.SSO) return true;

          // Override - check against specified plan
          if (checkWithPlan && checkWithPlan === codename) return true;

          switch (codename) {
            case BillingPlanTier.Lead:
              // Lead has all the features
              return true;
            case BillingPlanTier.Scale:
            // No codename means company is on trial. Trial has Scale plan access
            // eslint-disable-next-line no-fallthrough
            case null:
              // Scale has everything from Launch
              return (
                this.model.planFeatures[BillingPlanTier.Launch].some(f => f === feature) ||
                this.model.planFeatures[BillingPlanTier.Scale].some(f => f === feature)
              );
            case BillingPlanTier.Launch:
              return this.model.planFeatures[BillingPlanTier.Launch].some(f => f === feature);

            // This will be handled in future PRs as it's only done to make front-end work
            case BillingPlanTier.Optimize:
              return true;

            case BillingPlanTier.Grow:
              return (
                this.model.planFeatures[BillingPlanTier.Start].some(f => f === feature) ||
                this.model.planFeatures[BillingPlanTier.Grow].some(f => f === feature)
              );

            case BillingPlanTier.Start:
              return this.model.planFeatures[BillingPlanTier.Start].some(f => f === feature);

            default:
              throw new CodenameInvalidError(codename);
          }
        }),
      );
  }
}
